import Deal from "@mapmycustomers/shared/types/entity/Deal";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";

import DealLockReason from "@app/enum/DealLockReason";

export interface DealLock {
  canDelete: boolean;
  canEdit: boolean; // the deal's own fields, custom fields included
  canMoveStage: boolean;
  reason: DealLockReason | undefined;
}

export type LockableDeal = {
  funnel: Pick<Funnel, "id" | "stageMovesLocked">;
  stage: Pick<Stage, "dealsReadOnly" | "id">;
};

// A deal the screen knows only by id, looked up for its lock (fetchDealRefs)
export type LockableDealRef = LockableDeal & Pick<Deal, "id">;

// Locked if either the org's funnels or the deal's own flags say so: the funnels list may predate a
// lock the freshly fetched deal already carries, and the Kanban board's aggregated deals carry no flags.
const getDealLock = (
  deal: LockableDeal,
  funnels: Pick<Funnel, "id" | "stageMovesLocked">[],
  funnelStages: Record<Funnel["id"], Pick<Stage, "dealsReadOnly" | "id">[]>
): DealLock => {
  const listedFunnel = funnels.find(({ id }) => id === deal.funnel?.id);
  const listedStage = funnelStages[deal.funnel?.id]?.find(({ id }) => id === deal.stage?.id);
  const reason =
    listedStage?.dealsReadOnly || deal.stage?.dealsReadOnly
      ? DealLockReason.STAGE_READ_ONLY
      : listedFunnel?.stageMovesLocked || deal.funnel?.stageMovesLocked
        ? DealLockReason.FUNNEL_LOCKED
        : undefined;

  return {
    canDelete: !reason,
    canEdit: reason !== DealLockReason.STAGE_READ_ONLY,
    canMoveStage: !reason,
    reason,
  };
};

// Whether an edit may go through: a stage change needs the stage to move, any other edit the fields.
export const canApplyDealEdit = (lock: DealLock, changesStage: boolean): boolean =>
  changesStage ? lock.canMoveStage : lock.canEdit;

export default getDealLock;
